import * as React from 'react';

import { lighten } from 'polished';
import styled from 'styled-components';

import { COLOR, SPACING } from '../../packages/constants';

export interface HelperModalProps {
    children: React.ReactNode;
    dataId: string;
    modalContent: React.ReactNode;
    position?: 'top' | 'bottom' | undefined;
    isOpen?: boolean;
    inline?: boolean;
    width?: number;
}

interface ModalContentProps {
    position?: 'top' | 'bottom' | undefined;
    width?: number;
}

interface StyledHelperModal {
    inline?: boolean;
}

const StyledHelperModal = styled.div`
    display: ${(props: StyledHelperModal): string => (props.inline ? 'inline' : '')};
    position: relative;
    width: 15px;
    height: 15px;
`;

const ModalContent = styled.div<ModalContentProps>`
    position: absolute;
    top: ${(props) => (props.position === 'top' ? 'auto' : '32px')};
    bottom: ${(props) => (props.position === 'top' ? '32px' : 'auto')};
    left: calc(50% - ((${(props) => `${props.width}px`} / 2) + ${SPACING.LARGE}));
    width: ${(props) => `${props.width}px`};
    padding: ${SPACING.LARGE};
    border: 1px solid ${COLOR.PRIMARY.BLACK};
    background-color: #fff;
    z-index: 99999;
    white-space: normal;

    &::before {
        border-top: 1px solid black;
        border-right: 1px solid black;
        background-color: white;
        width: 20px;
        height: 20px;
        content: '';
        position: absolute;
        top: ${(props: ModalContentProps): string => (props.position === 'top' ? 'auto' : '-11px')};
        bottom: ${(props: ModalContentProps): string => (props.position === 'top' ? '-11px' : 'auto')};
        left: calc(50% - 10px);
        transform: rotate(${(props: ModalContentProps): string => (props.position === 'top' ? '135deg' : '-45deg')});
    }
`;

const StyledButton = styled.button`
    border: 0;
    background-color: transparent;
    padding: 0;
    margin: 0;
    vertical-align: middle;
`;

const StyledQuestionMark = styled.i`
    background: ${COLOR.MAIN.PRIMARY};
    color: ${COLOR.PRIMARY.WHITE};
    cursor: pointer;
    font-weight: 700;
    font-style: normal;
    border-radius: 10px;
    text-align: center;
    line-height: 15px;
    display: inline-block;
    transform: translateY(-2px);
    width: 15px;
    height: 15px;
    vertical-align: middle;

    &:hover {
        background: ${lighten(0.2, COLOR.MAIN.PRIMARY)};
    }
`;

export const QuestionMark = () => <StyledQuestionMark>?</StyledQuestionMark>;

export const HelperModal = ({
    children,
    position = 'top',
    modalContent,
    inline,
    width = 420,
    dataId,
    isOpen = false,
    ...props
}: HelperModalProps) => {
    const elementRef = React.useRef<HTMLInputElement | null>(null);
    const [showContent, setShowContent] = React.useState(isOpen);
    const toggleContent = () => setShowContent(!showContent);

    React.useEffect(() => {
        function handleKeyListener(e: any) {
            if ((showContent && e.key === 'Escape') || (showContent && e.key === 'Tab')) {
                setShowContent(false);
            }
        }

        function handleClick(e: any) {
            if (showContent && elementRef.current && !elementRef.current.contains(e.target)) {
                setShowContent(false);
            }
        }

        document.addEventListener('keydown', handleKeyListener);
        document.addEventListener('click', handleClick);

        return function cleanupKeyListener() {
            document.removeEventListener('keydown', handleKeyListener);
            document.removeEventListener('click', handleClick);
        };
    }, [showContent, setShowContent]);

    return (
        <StyledHelperModal inline={inline} {...props} data-test-id={`helper-modal__${dataId}`}>
            <StyledButton onClick={toggleContent} type={'button'} data-test-id={`helper-modal__button-${dataId}`}>
                {children}
            </StyledButton>
            {showContent && (
                <>
                    <ModalContent
                        ref={elementRef}
                        position={position}
                        width={width}
                        data-test-id={`helper-modal__message-content-${dataId}`}
                    >
                        {modalContent}
                    </ModalContent>
                </>
            )}
        </StyledHelperModal>
    );
};
